import { useState } from "react";
import { Card, CardHeader } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { StatCard } from "../../design-system/StatCard";
import { Icon, type IconName } from "../../design-system/Icon";
import { LoadingState, ErrorState } from "../../components/States";
import { firstProblem, minLen } from "../../components/validators";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import type { EngineOverviewDto, ProductAction } from "../../ipc/types";
import { CopyDataButton } from "../../components/CopyDataButton";
import "../dashboard/dashboard.css";
import "../product-development/product-development.css";

/** Acciones disponibles según el estado mostrado en la fila. */
function actionsFor(statusLabel: string): ProductAction[] {
  if (statusLabel === "Activo") return ["deactivate", "archive"];
  if (statusLabel === "Borrador" || statusLabel === "Inactivo") return ["activate", "archive"];
  return [];
}
const ACTION_LABEL: Record<ProductAction, string> = { activate: "Activar", deactivate: "Desactivar", archive: "Archivar" };

function NewProductForm({ onCreate, onCancel, busy }: { onCreate: (v: { sku: string; name: string; unit: string }) => void; onCancel: () => void; busy: boolean }): JSX.Element {
  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("unit");
  const hint = firstProblem([
    { ok: minLen(sku, 2), msg: "Ingresa un SKU (mínimo 2 caracteres)" },
    { ok: minLen(name, 2), msg: "Ingresa un nombre" },
  ]);
  const valid = hint === null;
  return (
    <Card style={{ marginBottom: "var(--sp-5)" }}>
      <CardHeader title="Nuevo producto" subtitle="Se crea en estado Borrador; luego actívalo cuando esté listo" />
      <div className="card__body">
        <div className="pd-form">
          <div className="pd-field"><span className="pd-field__label">SKU</span>
            <input className="pd-input" value={sku} placeholder="Ej: ARO-LED-26" aria-label="SKU" onChange={(e) => setSku(e.target.value)} /></div>
          <div className="pd-field"><span className="pd-field__label">Unidad</span>
            <select className="pd-input" value={unit} aria-label="Unidad" onChange={(e) => setUnit(e.target.value)}>
              {["unit", "kg", "g", "l", "ml", "m", "cm", "box", "pack"].map((u) => <option key={u} value={u}>{u}</option>)}
            </select></div>
          <div className="pd-field pd-form__full"><span className="pd-field__label">Nombre</span>
            <input className="pd-input" value={name} placeholder="Ej: Aro de luz LED 26cm con trípode" aria-label="Nombre" onChange={(e) => setName(e.target.value)} /></div>
        </div>
        <div className="pd-actions">
          {hint ? <span className="faint" style={{ fontSize: 12.5, marginRight: "auto" }}>{hint}</span> : null}
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
          <Button size="sm" icon="check" disabled={!valid || busy} onClick={() => onCreate({ sku: sku.trim(), name: name.trim(), unit })}>
            {busy ? "Creando…" : "Crear producto"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export function ProductScreen(): JSX.Element {
  const client = useIpc();
  const { state, reload } = useIpcQuery<EngineOverviewDto>((c) => c.getEngineOverview("product"), []);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  if (state.status === "loading") return <LoadingState />;
  if (state.status === "error") return <ErrorState message={state.error.message} onRetry={reload} />;

  const data = state.data;

  const create = async (v: { sku: string; name: string; unit: string }): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.createProduct(v);
    setBusy(false);
    if (res.ok && res.value.ok) { setShowForm(false); setMsg(`Producto "${v.name}" creado.`); reload(); }
    else setMsg(res.ok ? res.value.message ?? "No se pudo crear" : res.error.message);
  };
  const act = async (id: string, action: ProductAction): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.setProductStatus(id, action);
    setBusy(false);
    if (res.ok && res.value.ok) reload();
    else setMsg(res.ok ? res.value.message ?? "Acción no permitida" : res.error.message);
  };

  return (
    <div>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="row" style={{ gap: "var(--sp-4)" }}>
            <div className="sidebar__logo" style={{ width: 44, height: 44 }}><Icon name="product" size={22} /></div>
            <div>
              <h1 className="pagehead__title">Producto</h1>
              <p className="pagehead__sub">Catálogo maestro. Crea y gestiona tus productos; se guardan en tu equipo.</p>
            </div>
          </div>
          <div className="row">
            <CopyDataButton data={{ title: data.title, columns: data.columns, rows: data.rows, metrics: data.metrics }} />
            <Button size="sm" icon="product" onClick={() => setShowForm((s) => !s)}>Nuevo producto</Button>
          </div>
        </div>
      </div>

      <div className="kpis" style={{ marginTop: 0 }}>
        {data.metrics.map((m) => (
          <StatCard key={m.label} label={m.label} value={m.value} icon={m.icon as IconName} accent={m.accent}
            {...(typeof m.deltaPct === "number" ? { deltaPct: m.deltaPct } : {})} />
        ))}
      </div>

      {showForm ? <div style={{ marginTop: "var(--sp-5)" }}><NewProductForm onCreate={create} onCancel={() => setShowForm(false)} busy={busy} /></div> : null}

      <Card style={{ marginTop: "var(--sp-5)" }}>
        <CardHeader title="Catálogo" subtitle={`${data.rows.length} productos`} action={msg ? <span className="muted" style={{ fontSize: 12.5 }}>{msg}</span> : null} />
        <div className="card__body" style={{ padding: 0 }}>
          {data.rows.length === 0 ? (
            <div className="statecard"><Icon name="product" size={26} style={{ color: "var(--text-faint)" }} /><div className="strong">Aún no tienes productos</div><div className="faint" style={{ fontSize: 13 }}>Crea el primero con “Nuevo producto”.</div></div>
          ) : (
            <table className="table">
              <thead><tr><th>Producto</th><th>Detalle</th><th>Estado</th><th>Acciones</th></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id}>
                    <td><div className="strong">{r.primary}</div></td>
                    <td className="muted">{r.secondary}</td>
                    <td><Badge tone={r.status.tone} dot>{r.status.label}</Badge></td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        {actionsFor(r.status.label).map((a) => (
                          <Button key={a} variant={a === "archive" ? "ghost" : "secondary"} size="sm" disabled={busy} onClick={() => act(r.id, a)}>{ACTION_LABEL[a]}</Button>
                        ))}
                        {actionsFor(r.status.label).length === 0 ? <span className="faint" style={{ fontSize: 12 }}>—</span> : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}
